import { type FC, useEffect, useState } from 'react';

import type { WithdrawOutcome } from '../../../lib/cancellation-sync';
import { formatClock } from '../../utils/voice-note/validate';

interface IWithdrawRingProps {
	/** Epoch ms the server stops accepting a withdraw (`withdrawableUntil`). */
	deadline: number;
	/** Full window length, for the ring's proportion. */
	windowMs: number;
	onWithdraw: () => Promise<WithdrawOutcome>;
	now?: () => number;
}

const R = 15;
const C = 2 * Math.PI * R;

/**
 * "Withdraw · 9:41 left". The countdown runs off the SERVER's deadline, never a client guess of
 * when the window opened, so the ring and the server close together.
 */
export const WithdrawRing: FC<IWithdrawRingProps> = ({ deadline, windowMs, onWithdraw, now = Date.now }) => {
	const [t, setT] = useState(now);
	const [confirming, setConfirming] = useState(false);
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState('');

	const remaining = Math.max(0, deadline - t);
	const expired = remaining <= 0;
	useEffect(() => {
		if (expired) return;
		const id = window.setInterval(() => setT(now()), 1000);
		return () => window.clearInterval(id);
	}, [expired, now]);

	if (remaining <= 0) {
		return <p className='pc-withdraw__closed'>{message || 'Withdraw window closed — ask your party admin to reinstate.'}</p>;
	}

	const fraction = windowMs > 0 ? Math.min(1, remaining / windowMs) : 0;
	const left = formatClock(remaining / 1000);

	const confirm = async () => {
		setBusy(true);
		const out = await onWithdraw();
		setBusy(false);
		setMessage(out.message);
		if (!out.ok) setConfirming(false);
	};

	if (confirming) {
		return (
			<div
				className='pc-withdraw pc-withdraw--confirm'
				role='group'
				aria-label='Confirm withdraw'
			>
				<p className='pc-withdraw__q'>Withdraw this report?</p>
				<div className='pc-withdraw__actions'>
					<button
						type='button'
						className='pc-btn pc-btn--quiet-danger'
						onClick={confirm}
						disabled={busy}
					>
						{busy ? 'Withdrawing…' : 'Withdraw'}
					</button>
					<button
						type='button'
						className='pc-btn pc-btn--ghost'
						onClick={() => setConfirming(false)}
						disabled={busy}
					>
						Keep
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className='pc-withdraw'>
			<button
				type='button'
				className='pc-withdraw__btn'
				onClick={() => {
					setMessage('');
					setConfirming(true);
				}}
			>
				<svg
					className='pc-withdraw__ring'
					width='36'
					height='36'
					viewBox='0 0 36 36'
					aria-hidden='true'
				>
					<circle
						className='pc-withdraw__track'
						cx='18'
						cy='18'
						r={R}
					/>
					<circle
						className='pc-withdraw__arc'
						cx='18'
						cy='18'
						r={R}
						strokeDasharray={C}
						strokeDashoffset={C * (1 - fraction)}
					/>
				</svg>
				<span>
					Withdraw · <span className='pc-withdraw__left'>{left}</span> left
				</span>
			</button>
			{message ? (
				<p
					className='pc-withdraw__msg'
					role='status'
				>
					{message}
				</p>
			) : null}
		</div>
	);
};

export default WithdrawRing;
